import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Heart, LayoutGrid, Clock, Sparkles } from 'lucide-react';
import API from '@/utils/api';
import AppLogo from '@/components/AppLogo';

/**
 * OnboardingFlow - Calm, reassuring onboarding for new users
 * 
 * 4 screens as specified:
 * 1. Welcome - What the app is
 * 2. What it does - Feature overview
 * 3. Reassurance - No pressure to add everything now
 * 4. Finish - Start organizing
 * 
 * Skip button on every screen. Never shows again after completion or skip.
 */

const ONBOARDING_SCREENS = [
  {
    id: 'welcome',
    icon: Heart,
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-500',
    title: "Welcome to For Get Me Not",
    body: "Helps you keep track of everything in one calm place.",
    buttonText: 'Continue',
  },
  {
    id: 'features',
    icon: LayoutGrid,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    title: "Everything you need",
    features: [
      { name: 'Medical', color: 'bg-pink-100 text-pink-700' },
      { name: 'Important Dates', color: 'bg-blue-100 text-blue-700' },
      { name: 'Documents', color: 'bg-slate-100 text-slate-700' },
      { name: 'Car Maintenance', color: 'bg-amber-100 text-amber-700' },
      { name: 'Shopping', color: 'bg-emerald-100 text-emerald-700' },
      { name: 'Calendar', color: 'bg-purple-100 text-purple-700' },
    ],
    footer: "Calendar automatically connects everything",
    buttonText: 'Continue',
  },
  {
    id: 'reassurance',
    icon: Clock,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-500',
    title: "No rush",
    body: "You don't need to enter everything now. Add things gradually as life happens.",
    buttonText: 'Continue',
  },
  {
    id: 'finish',
    icon: Sparkles,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-500',
    title: "You're all set!",
    body: "Your calm place for everything important.",
    buttonText: 'Start organizing',
    isFinal: true,
  },
];

const OnboardingFlow = ({ onComplete }) => {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const screen = ONBOARDING_SCREENS[currentScreen];

  const completeOnboarding = async () => {
    setIsSaving(true);
    try {
      await API.put('/settings', { onboarding_completed: true });
    } catch (error) {
      console.error('Failed to save onboarding state:', error);
      // Continue anyway - never trap user in onboarding
    }
    
    if (onComplete) {
      onComplete();
    }
  };

  const handleSkip = () => {
    setIsExiting(true);
    setTimeout(() => {
      completeOnboarding();
    }, 200);
  };

  const handleContinue = () => {
    if (screen.isFinal) {
      setIsExiting(true);
      setTimeout(() => {
        completeOnboarding();
      }, 200);
    } else {
      setCurrentScreen(prev => prev + 1);
    }
  };

  const Icon = screen.icon;

  return (
    <div 
      className={`
        min-h-screen bg-gradient-to-b from-blue-50 via-white to-rose-50
        flex flex-col transition-opacity duration-200
        ${isExiting ? 'opacity-0' : 'opacity-100'}
      `}
      data-testid="onboarding-flow"
    >
      {/* Header with Skip Button */}
      <header className="flex items-center justify-between p-4">
        <AppLogo size="sm" />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSkip}
          disabled={isSaving}
          className="text-slate-400 hover:text-slate-600 font-medium"
          data-testid="onboarding-skip"
        >
          Skip
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-24">
        {/* Progress Dots */}
        <div className="flex gap-2 mb-12">
          {ONBOARDING_SCREENS.map((_, index) => (
            <div
              key={index}
              className={`
                w-2 h-2 rounded-full transition-all duration-300
                ${index === currentScreen 
                  ? 'w-6 bg-blue-500' 
                  : index < currentScreen 
                    ? 'bg-blue-300' 
                    : 'bg-slate-200'
                }
              `}
              data-testid={`onboarding-dot-${index}`}
            />
          ))}
        </div>

        {/* Icon */}
        <div 
          className={`
            w-20 h-20 rounded-3xl flex items-center justify-center mb-8
            ${screen.iconBg}
          `}
        >
          <Icon className={`h-10 w-10 ${screen.iconColor}`} />
        </div>

        {/* Title */}
        <h1 
          className="text-2xl sm:text-3xl font-semibold text-slate-800 text-center mb-4 max-w-md"
          data-testid="onboarding-title"
        >
          {screen.title}
        </h1>

        {/* Body Text */}
        {screen.body && (
          <p className="text-lg text-slate-500 text-center max-w-sm mb-8 leading-relaxed">
            {screen.body}
          </p>
        )}

        {/* Features List (Screen 2) */}
        {screen.features && (
          <div className="mb-6 w-full max-w-sm">
            <div className="flex flex-wrap gap-2 justify-center mb-4">
              {screen.features.map((feature, idx) => (
                <span 
                  key={idx}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium ${feature.color}`}
                >
                  {feature.name}
                </span>
              ))}
            </div>
            {screen.footer && (
              <p className="text-sm text-slate-400 text-center italic">
                {screen.footer}
              </p>
            )}
          </div>
        )}

        {/* Continue Button */}
        <Button
          onClick={handleContinue}
          disabled={isSaving}
          size="lg"
          className={`
            px-8 py-6 text-base font-medium rounded-2xl min-w-[200px]
            ${screen.isFinal 
              ? 'bg-emerald-500 hover:bg-emerald-600' 
              : 'bg-blue-500 hover:bg-blue-600'
            }
          `}
          data-testid="onboarding-continue"
        >
          {isSaving ? 'Loading...' : screen.buttonText}
        </Button>
      </main>

      {/* Bottom Safe Area */}
      <div className="h-8" />
    </div>
  );
};

export default OnboardingFlow;
